import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { getNotifications, getUnreadNotificationCount, savePushSubscription } from '../../api/notificationService';

const NOTIFICATION_POLL_INTERVAL_MS = 30_000;
const NOTIFICATION_TITLE = 'BetMate';
const PUSH_SERVICE_WORKER_PATH = '/push-service-worker.js';
const PUSH_UNAVAILABLE_SECURE_CONTEXT_MESSAGE = 'Push требует защищённое HTTPS-соединение.';
const PUSH_UNAVAILABLE_VAPID_KEY_MESSAGE = 'VAPID public key не передан во frontend.';
const PUSH_UNAVAILABLE_NOTIFICATION_API_MESSAGE = 'Браузер не поддерживает Browser Notifications API.';
const PUSH_UNAVAILABLE_SERVICE_WORKER_MESSAGE = 'Браузер не поддерживает Service Worker.';
const PUSH_UNAVAILABLE_API_MESSAGE = 'Браузер не поддерживает Web Push API.';
const PUSH_PERMISSION_DENIED_MESSAGE = 'Разрешение на уведомления не выдано.';
const NotificationContext = createContext(null);

export const NotificationProvider = ({ children }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const knownNotificationIds = useRef(new Set());
  const initialized = useRef(false);

  const refreshUnreadCount = useCallback(async () => {
    try {
      setUnreadCount(await getUnreadNotificationCount());
    } catch {
      setUnreadCount(0);
    }
  }, []);

  const refreshBrowserNotifications = useCallback(async () => {
    try {
      const notifications = await getNotifications();
      const unreadNotifications = notifications.filter((notification) => notification.unread);
      const newNotifications = initialized.current
        ? unreadNotifications.filter((notification) => !knownNotificationIds.current.has(notification.id))
        : [];

      unreadNotifications.forEach((notification) => knownNotificationIds.current.add(notification.id));
      initialized.current = true;

      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        newNotifications.forEach((notification) => new Notification(NOTIFICATION_TITLE, { body: notification.message }));
      }
    } catch {
      // Ошибка polling не должна влиять на работу портала.
    }
  }, []);

  useEffect(() => {
    refreshUnreadCount();
    refreshBrowserNotifications();
    const intervalId = window.setInterval(() => {
      refreshUnreadCount();
      refreshBrowserNotifications();
    }, NOTIFICATION_POLL_INTERVAL_MS);
    return () => window.clearInterval(intervalId);
  }, [refreshBrowserNotifications, refreshUnreadCount]);

  const enablePushNotifications = useCallback(async () => {
    const publicKey = window._env_?.REACT_APP_WEB_PUSH_VAPID_PUBLIC_KEY;
    validatePushSupport(publicKey);

    const permission = await Notification.requestPermission();
    if (permission !== 'granted') {
      throw new Error(PUSH_PERMISSION_DENIED_MESSAGE);
    }

    const registration = await navigator.serviceWorker.register(PUSH_SERVICE_WORKER_PATH);
    const existingSubscription = await registration.pushManager.getSubscription();
    const subscription = existingSubscription || await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    const subscriptionJson = subscription.toJSON();
    await savePushSubscription({
      endpoint: subscription.endpoint,
      p256dh: subscriptionJson.keys?.p256dh,
      auth: subscriptionJson.keys?.auth,
    });
  }, []);

  const value = useMemo(() => ({ unreadCount, refreshUnreadCount, enablePushNotifications }), [
    unreadCount,
    refreshUnreadCount,
    enablePushNotifications,
  ]);

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

function validatePushSupport(publicKey) {
  if (!window.isSecureContext) {
    throw new Error(PUSH_UNAVAILABLE_SECURE_CONTEXT_MESSAGE);
  }
  if (!publicKey) {
    throw new Error(PUSH_UNAVAILABLE_VAPID_KEY_MESSAGE);
  }
  if (typeof Notification === 'undefined') {
    throw new Error(PUSH_UNAVAILABLE_NOTIFICATION_API_MESSAGE);
  }
  if (!('serviceWorker' in navigator)) {
    throw new Error(PUSH_UNAVAILABLE_SERVICE_WORKER_MESSAGE);
  }
  if (!('PushManager' in window)) {
    throw new Error(PUSH_UNAVAILABLE_API_MESSAGE);
  }
}

function urlBase64ToUint8Array(value) {
  const padding = '='.repeat((4 - (value.length % 4)) % 4);
  const base64 = (value + padding).replace(/-/g, '+').replace(/_/g, '/');
  const decoded = window.atob(base64);
  return Uint8Array.from(decoded, (character) => character.charCodeAt(0));
}

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    return {
      unreadCount: 0,
      refreshUnreadCount: async () => {},
      enablePushNotifications: async () => {},
    };
  }
  return context;
};
